import React, { useContext, useEffect, useReducer, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { Store } from '../Store';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import { getError } from '../Util';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MeaasgeBox';
import CardHeader from '../components/CardHeadder';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };
    case 'UPLOAD_REQUEST':
      return { ...state, loadingUpload: true };
    case 'UPLOAD_SUCCESS':
      return { ...state, loadingUpload: false };
    case 'UPLOAD_FAIL':
      return { ...state, loadingUpload: false, errorUpload: action.payload };
    default:
      return state;
  }
};

export default function FabricEditScreen() {
  const navigate = useNavigate();
  const params = useParams();
  const { id: fabricId } = params;

  const { state } = useContext(Store);
  const { userInfo } = state;

  const [{ loading, error, loadingUpdate, loadingUpload }, dispatch] =
    useReducer(reducer, {
      loading: true,
      error: '',
    });

  const [name, setName] = useState('');
  const [fabricCode, setFabricCode] = useState('');
  const [image, setImage] = useState('');
  const [description, setDescription] = useState(['']);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        if (fabricId) {
          const { data } = await api.get(`/api/fabrics/${fabricId}`);
          setName(data.name);
          setFabricCode(data.fabricCode); // Set fabricCode from fetched data
          setImage(data.image);
          const safeDescription = Array.isArray(data.description) 
            ? data.description 
            : (typeof data.description === 'string' ? [data.description] : ['']);
          setDescription(safeDescription);
        } else {
          // Initialize for create mode
          setName('');
          setFabricCode('');
          setImage('');
          setDescription(['']);
        }
        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchData();
  }, [fabricId]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      dispatch({ type: 'UPDATE_REQUEST' });
      if (fabricId) {
        // Update existing fabric
        await api.put(
          `/api/fabrics/${fabricId}`,
          { _id: fabricId, name, fabricCode, image, description },
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        toast.success('Fabric updated successfully');
      } else {
        // Create new fabric
        await api.post(
          '/api/fabrics',
          { name, fabricCode, image, description },
          { headers: { Authorization: `Bearer ${userInfo.token}` } }
        );
        toast.success('Fabric created successfully');
      }
      dispatch({ type: 'UPDATE_SUCCESS' });
      navigate('/admin/fabrics');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  };

  const uploadFileHandler = async (e) => {
    const file = e.target.files[0];
    const bodyFormData = new FormData();
    bodyFormData.append('file', file);
    try {
      dispatch({ type: 'UPLOAD_REQUEST' });
      const { data } = await api.post('/api/upload', bodyFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      setImage(data.secure_url);
      dispatch({ type: 'UPLOAD_SUCCESS' });
      toast.success('Image uploaded successfully. Click Update to apply it!');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPLOAD_FAIL', payload: getError(err) });
    }
  };

  const handleDescriptionChange = (index, value) => {
    const newDesc = [...description];
    newDesc[index] = value;
    setDescription(newDesc);
  };

  const handleAddDescription = () => {
    setDescription([...description, '']);
  };

  const handleRemoveDescription = (index) => {
    const newDesc = description.filter((_, i) => i !== index);
    setDescription(newDesc);
  };

  return (
    <div>
      <CardHeader />
      <Container fluid="md" className="mt-5 py-5">
        <Helmet>
          <title>Edit Fabric {fabricId}</title>
        </Helmet>
        <h3 style={{ fontSize: '2rem' }}>
          Edit Fabric <small>({fabricId})</small>
        </h3>

        {loading ? (
          <LoadingBox />
        ) : error ? (
          <MessageBox variant="danger">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Fabric Name</Form.Label>
              <Form.Control
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="fabricCode">
              <Form.Label>Fabric Code</Form.Label>
              <Form.Control
                value={fabricCode}
                onChange={(e) => setFabricCode(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="image">
              <Form.Label>Image URL</Form.Label>
              <Form.Control
                value={image}
                onChange={(e) => setImage(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="imageFile">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control type="file" onChange={uploadFileHandler} />
              {loadingUpload && <LoadingBox />}
            </Form.Group>

            <Form.Label>Description</Form.Label>
            {description.map((desc, index) => (
              <Form.Group key={index} className="mb-3">
                <Form.Control
                  as="textarea"
                  value={desc}
                  onChange={(e) =>
                    handleDescriptionChange(index, e.target.value)
                  }
                  required
                />
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemoveDescription(index)}
                  className="mt-1"
                >
                  Remove
                </Button>
              </Form.Group>
            ))}
            <Button variant="secondary" onClick={handleAddDescription}>
              Add Description Line
            </Button>

            <div className="mt-3">
              <Button type="submit" disabled={loadingUpdate}>
                Update
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </div>
  );
}
